/**
 * Auth slice - Redux state for auth
 * Fast reads from store, single API orchestration
 */
import { createSlice } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { AUTH, ROLES } from '../../api/endpoints';

const getInitialState = () => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      return {
        user,
        permissions: [],
        isAuthenticated: true,
        loading: true,
        permissionsReady: false,
      };
    } catch (e) {
      // Invalid JSON in localStorage, ignore
    }
  }
  return {
    user: null,
    permissions: [],
    isAuthenticated: false,
    loading: true,
    permissionsReady: false,
  };
};

const initialState = getInitialState();

/** Normalize permission name for consistent matching (dba-create, dba create, dba_create) */
const normalizePermission = (name) =>
  (name || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

const extractPermissionNames = (response) => {
  const permsData = response?.data?.data?.permissions || response?.data || [];
  return (Array.isArray(permsData) ? permsData : [])
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Extract permission names from user object or profile response */
const extractUserPermissionNames = (userData) => {
  const perms = userData?.permissions ?? userData?.data?.permissions ?? [];
  const arr = Array.isArray(perms) ? perms : [];
  return arr
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Extract permissions from /auth/profile response - supports multiple shapes */
const extractProfilePermissions = (profileResponse) => {
  const data = profileResponse?.data ?? profileResponse;
  const user = data?.user ?? data?.data;
  const perms =
    user?.permissions ??
    data?.permissions ??
    data?.data?.permissions ??
    [];
  const arr = Array.isArray(perms) ? perms : [];
  return arr
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Merge role permissions with user permissions (deduplicated) */
const mergePermissions = (rolePerms, userPerms) => {
  const set = new Set([...(rolePerms || []), ...(userPerms || [])]);
  return [...set];
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, { payload }) => {
      state.user = payload?.user ?? payload;
      state.isAuthenticated = !!payload;
      state.loading = false;
    },
    setPermissions: (state, { payload }) => {
      state.permissions = payload || [];
    },
    setPermissionsReady: (state, { payload }) => {
      state.permissionsReady = payload !== false;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload ?? true;
    },
    logout: (state) => {
      state.user = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.loading = false;
      state.permissionsReady = false;
    },
    refreshPermissionsSuccess: (state, { payload }) => {
      state.permissions = payload || [];
    },
  },
});

export const {
  setCredentials,
  setPermissions,
  setPermissionsReady,
  setLoading,
  logout: logoutAction,
  refreshPermissionsSuccess,
} = authSlice.actions;

export default authSlice.reducer;

// Thunks - single place for auth API orchestration
export const checkAuth = () => async (dispatch, getState) => {
  const token = localStorage.getItem('token');
  if (!token) {
    dispatch(setLoading(false));
    dispatch(setPermissionsReady(true));
    return;
  }

  try {
    dispatch(setLoading(true));
    dispatch(setPermissionsReady(false));
    const profileRes = await api.get(AUTH.PROFILE);
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;
    const hydratedUser = getState().auth.user;

    const idsMatch = (a, b) => {
      if (!a || !b) return false;
      const idA = a?.id ?? a?.uuid;
      const idB = b?.id ?? b?.uuid;
      if (idA != null && idB != null) return String(idA) === String(idB);
      return (a?.email || a?.username) === (b?.email || b?.username);
    };

    if (userData && idsMatch(userData, hydratedUser)) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    } else if (!hydratedUser && userData) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }

    const displayUser = userData && idsMatch(userData, hydratedUser) ? userData : hydratedUser;
    const userId = displayUser?.id ?? displayUser?.uuid;
    const roleId = displayUser?.role?.id || displayUser?.roles?.[0]?.id;

    let profilePerms = extractProfilePermissions(profileRes);
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {
        // Role fetch may fail; use profile perms only
      }
    }

    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));

    if (permissions.length === 0 && userId) {
      toast.warn('User has no permissions assigned');
    }
  } catch (error) {
    console.error('Auth check failed:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch(logoutAction());
  } finally {
    dispatch(setLoading(false));
    dispatch(setPermissionsReady(true));
  }
};

export const loginUser = (credentials) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await api.post(AUTH.LOGIN, credentials);
    const data = response?.data ?? response;

    const userData = data?.user ?? data?.data?.user ?? data;
    const token =
      data?.access_token ??
      data?.token ??
      data?.data?.access_token ??
      data?.data?.token;

    if (!token) {
      throw new Error('No token received from server. Please try again.');
    }

    localStorage.setItem('token', token);
    if (userData && typeof userData === 'object') {
      localStorage.setItem('user', JSON.stringify(userData));
    }

    dispatch(setCredentials(userData || { token }));

    const profileRes = await api.get(AUTH.PROFILE);
    const profileData = profileRes?.data ?? profileRes;
    const profileUser = profileData?.user ?? profileData?.data ?? profileData;
    if (profileUser) {
      dispatch(setCredentials(profileUser));
      localStorage.setItem('user', JSON.stringify(profileUser));
    }

    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = profileUser?.role?.id || profileUser?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));

    return { data };
  } catch (error) {
    const msg = error.apiMessage || error.response?.data?.message || error?.message || 'Login failed';
    throw new Error(msg);
  } finally {
    dispatch(setLoading(false));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await api.post(AUTH.LOGOUT);
  } catch (e) {
    // ignore
  }
  localStorage.clear();
  dispatch(logoutAction());
  toast.success('Logged out');
};

export const refreshPermissions = () => async (dispatch) => {
  try {
    const profileRes = await api.get(AUTH.PROFILE);
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;

    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = userData?.role?.id || userData?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(refreshPermissionsSuccess(permissions));
  } catch (e) {
    console.error('Failed to refresh permissions:', e);
  }
};

export const getProfile = () => async (dispatch, getState) => {
  try {
    const { data } = await api.get(AUTH.PROFILE);
    return data?.user ?? data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

/** Fetch profile and update Redux auth state (user + permissions) - returns user data */
export const refreshAuthProfile = () => async (dispatch) => {
  try {
    const profileRes = await api.get(AUTH.PROFILE);
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;
    if (userData) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }
    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = userData?.role?.id || userData?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    return userData;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

export const updateProfile = (formData) => async () => {
  formData.append('_method', 'PUT');
  try {
    const { data } = await api.post(AUTH.PROFILE_UPDATE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Profile update failed');
  }
};

export const forgotPassword = (payload) => async () => {
  try {
    const { data } = await api.post(AUTH.FORGOT_PASSWORD, payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Forgot password failed');
  }
};

export const resetPassword = (payload) => async () => {
  try {
    const { data } = await api.post(AUTH.RESET_PASSWORD, payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Reset password failed');
  }
};
